/**
 * @sazeo/sdk — Budgets hooks
 *
 * TanStack Query wrappers for the budgets addon. All calls route through
 * the Edge Function at `/addons/budgets/*` guarded by
 * requireAddonEnabled() and enforced by RLS via the caller's JWT.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Budget {
  id: string;
  tenant_id: string;
  name: string;
  department: string | null;
  period_start: string;
  period_end: string;
  amount: number;
  currency: string;
  status: 'draft' | 'pending' | 'active' | 'approved' | 'rejected' | 'closed';
  notes: string | null;
  created_by: string | null;
  approved_by: string | null;
  approval_comment: string | null;
  approval_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BudgetItem {
  id: string;
  tenant_id: string;
  budget_id: string;
  description: string;
  category: 'salaries' | 'equipment' | 'software' | 'travel' | 'marketing' | 'other';
  planned: number;
  actual: number;
  created_at: string;
  updated_at: string;
}

export interface BudgetCreateInput {
  name: string;
  department?: string | null;
  period_start: string;
  period_end: string;
  amount: number;
  currency?: string;
  notes?: string | null;
}

export interface BudgetUpdateInput extends Partial<BudgetCreateInput> {
  status?: Budget['status'];
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const budgetKeys = {
  all: ['budgets'] as const,
  list: (tenantId: string) => [...budgetKeys.all, 'list', tenantId] as const,
  items: (budgetId: string) => [...budgetKeys.all, 'items', budgetId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useBudgets(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: budgetKeys.list(tenantId),
    queryFn: () => apiFetch<Budget[]>(supabase, 'addons/budgets/budgets'),
    enabled: Boolean(tenantId),
  });
}

export function useBudgetItems(supabase: SupabaseClientLike, tenantId: string, budgetId: string) {
  return useQuery({
    queryKey: budgetKeys.items(budgetId),
    queryFn: () => apiFetch<BudgetItem[]>(supabase, `addons/budgets/budgets/${budgetId}/items`),
    enabled: Boolean(tenantId) && Boolean(budgetId),
  });
}

export function useCreateBudget(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BudgetCreateInput) =>
      apiFetch<Budget>(supabase, 'addons/budgets/budgets', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: budgetKeys.list(tenantId) });
    },
  });
}

export function useUpdateBudget(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: BudgetUpdateInput }) =>
      apiFetch<Budget>(supabase, `addons/budgets/budgets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: budgetKeys.list(tenantId) });
    },
  });
}

export function useDeleteBudget(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/budgets/budgets/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: budgetKeys.list(tenantId) });
    },
  });
}

export function useApproveBudget(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      apiFetch<Budget>(supabase, `addons/budgets/budgets/${id}/approve`, {
        method: 'PATCH',
        body: JSON.stringify({ comment }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: budgetKeys.list(tenantId) });
    },
  });
}

export function useRejectBudget(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      apiFetch<Budget>(supabase, `addons/budgets/budgets/${id}/reject`, {
        method: 'PATCH',
        body: JSON.stringify({ comment }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: budgetKeys.list(tenantId) });
    },
  });
}
